import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { OpsAuditRecord, OpsAuditTargetType } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useAuditList } from '../api/useAudit.ts';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { OpsUserRef } from '../components/identity/OpsUserRef.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/governance/audit` — append-only ops audit log viewer.
 *
 * Dispatch Deck (epic #835): `ui/Table` (hairline dividers, no zebra),
 * `label`-tier visible filter labels, mono identifier/timestamp cells.
 *
 * Operator affordances:
 *   - Four independent text filters (action, target type, target id,
 *     ops user id). All combine via AND on the backend.
 *   - Pagination (limit=50). Offset-only; total comes from the response.
 *   - Click a row to expand metadata JSON (pretty-printed).
 *
 * Filters change the React Query cache key so back/forward navigation
 * between filter states rehydrates from cache. No write mutations on
 * this page; the audit log is immutable.
 */

const PAGE_LIMIT = 50;
const AUDIT_ACTOR_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/u;

/**
 * Radix Select items may not carry an empty-string value, so the
 * "any type" choice travels as this sentinel and maps back to '' in
 * filter state (which is what "no filter" means to the backend).
 */
const ANY_TARGET_TYPE = 'any';

const TARGET_TYPE_OPTIONS: ReadonlyArray<{
  readonly value: '' | OpsAuditTargetType;
  readonly labelKey: I18nKey;
}> = [
  { value: '', labelKey: 'audit.filter.target_type.any' },
  { value: 'account', labelKey: 'audit.filter.target_type.account' },
  { value: 'gift-card', labelKey: 'audit.filter.target_type.gift_card' },
  { value: 'gift-card-batch', labelKey: 'audit.filter.target_type.gift_card_batch' },
  { value: 'upstream', labelKey: 'audit.filter.target_type.upstream' },
  {
    value: 'upstream-funding-batch',
    labelKey: 'audit.filter.target_type.upstream_funding_batch',
  },
  { value: 'model', labelKey: 'audit.filter.target_type.model' },
  { value: 'failed-deduction', labelKey: 'audit.filter.target_type.failed_deduction' },
  { value: 'exchange-rate-policy', labelKey: 'audit.filter.target_type.exchange_rate_policy' },
  { value: 'payment-order', labelKey: 'audit.filter.target_type.payment_order' },
  { value: 'payment-provider', labelKey: 'audit.filter.target_type.payment_provider' },
];

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/**
 * Map an audit row's `target_type` to the console page that shows that
 * entity. Returns `null` when there is no destination — the cell then
 * renders plain text (issue #854).
 *
 * `target_type` is free-form on the wire (the contracts union at
 * `packages/contracts/src/internal-ops-audit.ts` is documented as
 * non-exhaustive), so the literals matched here are the exact strings
 * the backend writers hardcode. Renaming a literal in any of these
 * source files silently breaks the corresponding link:
 *
 *   - 'account'               apps/ops-api/src/routes/v1-ops-admin.ts,
 *                             apps/platform-api/src/routes/internal-admin.ts
 *   - 'upstream'              apps/ops-api/src/routes/v1-ops-upstreams.ts
 *   - 'model'                 apps/platform-api/src/routes/internal-models.ts
 *                             (target_id is the `vendor/model` composite key)
 *   - 'message_trace'         apps/ops-api/src/routes/v1-ops-message-traces.ts
 *   - 'request_lifecycle'     apps/ops-api/src/routes/v1-ops-request-lifecycle.ts
 *                             (target_id is the gateway request id; the
 *                             requests page reads `?requestId=` from the URL)
 *   - 'exchange-rate-policy'  apps/platform-api/src/routes/internal-pricing.ts
 *   - 'tenant_routing'        apps/ops-api/src/routes/v1-ops-tenant-routing.ts
 *   - 'router-client-session' apps/ops-api/src/routes/v1-ops-router-client-identities.ts
 *   - 'upstream_error_policy_rule' / 'upstream_error_observation'
 *                             apps/ops-api/src/routes/v1-ops-upstream-error-policy.ts
 *
 * Deliberately unlinked (no detail page, and the list page cannot focus
 * a specific row from the URL): 'gift-card' (target_id is the code),
 * 'gift-card-batch', 'upstream-funding-batch' / 'upstream_funding_batch',
 * 'failed-deduction', 'payment-order' / 'payment_order',
 * 'payment-provider'. Unknown types also render plain.
 */
function auditTargetHref(targetType: string, targetId: string): string | null {
  switch (targetType) {
    case 'account':
      return `/customers/accounts/${encodeURIComponent(targetId)}`;
    case 'upstream':
      return `/supply/upstreams/${encodeURIComponent(targetId)}`;
    case 'model': {
      // target_id is the `vendor/model` composite key; both halves must
      // be non-empty to form a valid detail route.
      const slash = targetId.indexOf('/');
      if (slash <= 0 || slash === targetId.length - 1) return null;
      const vendor = targetId.slice(0, slash);
      const model = targetId.slice(slash + 1);
      return `/supply/models/${encodeURIComponent(vendor)}/${encodeURIComponent(model)}`;
    }
    case 'message_trace':
    case 'request_lifecycle':
      return `/customers/requests?requestId=${encodeURIComponent(targetId)}`;
    case 'exchange-rate-policy':
      return '/finance/fx-policy';
    case 'tenant_routing':
      return '/supply/tenant-routing';
    case 'router-client-session':
      return '/governance/router-client-identities';
    case 'upstream_error_policy_rule':
    case 'upstream_error_observation':
      return '/governance/error-policy';
    default:
      return null;
  }
}

/**
 * Pretty-print a metadata JSON string. Falls back to the raw string if
 * the payload doesn't parse — this should never happen with data we
 * write ourselves, but the audit table's `metadata` is TEXT so any
 * content is technically legal. Don't crash the row if an out-of-band
 * writer puts malformed JSON in.
 */
function prettyMetadata(raw: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return raw;
  }
}

interface RowProps {
  readonly row: OpsAuditRecord;
  readonly expanded: boolean;
  readonly onToggle: (id: string) => void;
}

function AuditRow({ row, expanded, onToggle }: RowProps): JSX.Element {
  const t = useT();
  let gatewayOwner: string | null = null;
  try {
    const metadata: unknown = JSON.parse(row.metadata);
    if (row.target_type === 'gateway_mutation' &&
      metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata)) {
      const facts = metadata as Record<string, unknown>;
      const owner = facts.trusted_owner_actor;
      const verifier = facts.verified_human_actor;
      if (typeof owner === 'string' && AUDIT_ACTOR_ID_PATTERN.test(owner) &&
        typeof verifier === 'string' && AUDIT_ACTOR_ID_PATTERN.test(verifier) &&
        verifier === row.ops_user_id && owner !== row.ops_user_id) {
        gatewayOwner = owner;
      }
    }
  } catch {
    // Historical malformed metadata must not hide the recorded audit identity.
  }
  const targetHref = auditTargetHref(row.target_type, row.target_id);
  return (
    <>
      <Tr
        data-testid={`audit-row-${row.id}`}
        className="cursor-pointer"
        onClick={() => onToggle(row.id)}
      >
        <Td
          mono
          title={formatLocalTimestamp(row.created_at, { seconds: true })}
          data-testid={`audit-row-${row.id}-created`}
        >
          {formatLocalTimestamp(row.created_at, { seconds: true })}
        </Td>
        <Td emphasis data-testid={`audit-row-${row.id}-action`}>
          {row.action}
        </Td>
        <Td data-testid={`audit-row-${row.id}-target-type`}>{row.target_type}</Td>
        <Td mono title={row.target_id} data-testid={`audit-row-${row.id}-target-id`}>
          {targetHref !== null ? (
            <Link
              to={targetHref}
              onClick={(e) => e.stopPropagation()}
              className="transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:underline"
              data-testid={`audit-row-${row.id}-target-link`}
            >
              {truncateId(row.target_id)}
            </Link>
          ) : (
            truncateId(row.target_id)
          )}
        </Td>
        <Td mono title={row.ops_user_id} data-testid={`audit-row-${row.id}-ops-user`}>
          {gatewayOwner === null ? <OpsUserRef id={row.ops_user_id} /> : (
            <div className="space-y-1 whitespace-normal">
              <div>{t('audit.actor.owner')} <OpsUserRef id={gatewayOwner} className="break-all" /></div>
              <div className="text-micro text-paper-mid">
                {t('audit.actor.verifier')} <OpsUserRef id={row.ops_user_id} className="break-all" />
              </div>
            </div>
          )}
        </Td>
        <Td className="text-right">
          {expanded ? (
            <ChevronUp aria-hidden="true" className="ml-auto h-4 w-4 text-paper-low" />
          ) : (
            <ChevronDown aria-hidden="true" className="ml-auto h-4 w-4 text-paper-low" />
          )}
        </Td>
      </Tr>
      {expanded ? (
        <Tr interactive={false} data-testid={`audit-row-${row.id}-metadata`}>
          <Td colSpan={6}>
            <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg bg-well p-3 font-mono text-micro text-paper">
{prettyMetadata(row.metadata)}
            </pre>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

interface FilterState {
  action: string;
  targetType: string;
  targetId: string;
  opsUserId: string;
}

const EMPTY_FILTERS: FilterState = {
  action: '',
  targetType: '',
  targetId: '',
  opsUserId: '',
};

/**
 * Deep-link prefill (issue #856): other pages link here with
 * `?action=…&targetType=…&targetId=…` (see `lib/auditDeepLink.ts`).
 * Absent params fall back to the empty filter, so a bare
 * `/governance/audit` behaves exactly as before. `opsUserId` is
 * deliberately not URL-addressable — no caller needs it yet.
 */
function filtersFromSearchParams(params: URLSearchParams): FilterState {
  return {
    action: params.get('action')?.trim() ?? '',
    targetType: params.get('targetType')?.trim() ?? '',
    targetId: params.get('targetId')?.trim() ?? '',
    opsUserId: '',
  };
}

export function Audit(): JSX.Element {
  const t = useT();
  const [searchParams] = useSearchParams();
  // Draft state is edited in the form; `applied` is what actually
  // drives the query. Pattern matches the accounts list — typing in
  // the search field must not issue a query on every keystroke.
  // Both start from the URL query so deep links land pre-filtered.
  const [draft, setDraft] = useState<FilterState>(() => filtersFromSearchParams(searchParams));
  const [applied, setApplied] = useState<FilterState>(() => filtersFromSearchParams(searchParams));
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Re-sync from the URL when the query string changes while this page
  // stays mounted (the toast provider is global, so an operator can
  // click a "View audit log" toast action while already on this route
  // and React Router will not remount the component). Form submits do
  // not touch the URL, so this never clobbers hand-edited filters.
  const searchKey = searchParams.toString();
  const lastSearchKeyRef = useRef(searchKey);
  useEffect(() => {
    if (lastSearchKeyRef.current === searchKey) return;
    lastSearchKeyRef.current = searchKey;
    const next = filtersFromSearchParams(new URLSearchParams(searchKey));
    setDraft(next);
    setApplied(next);
    setOffset(0);
    setExpandedId(null);
  }, [searchKey]);

  const query = useAuditList({
    action: applied.action.length > 0 ? applied.action : undefined,
    target_type: applied.targetType.length > 0 ? applied.targetType : undefined,
    target_id: applied.targetId.length > 0 ? applied.targetId : undefined,
    ops_user_id: applied.opsUserId.length > 0 ? applied.opsUserId : undefined,
    limit: PAGE_LIMIT,
    offset,
  });

  const rows = useMemo(() => query.data?.audit ?? [], [query.data]);
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setApplied({
      action: draft.action.trim(),
      targetType: draft.targetType.trim(),
      targetId: draft.targetId.trim(),
      opsUserId: draft.opsUserId.trim(),
    });
    setOffset(0);
    setExpandedId(null);
  }

  function handleReset(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setOffset(0);
    setExpandedId(null);
  }

  function handleToggle(id: string): void {
    setExpandedId((current) => (current === id ? null : id));
  }

  return (
    <section data-testid="audit-page" className="flex flex-col gap-6">
      <PageHeader title={t('audit.title')} sub={t('audit.subtitle')} />

      <form
        onSubmit={handleSubmit}
        data-testid="audit-filters"
        className="flex flex-wrap items-end gap-3 rounded-lg border border-line-soft bg-card p-4"
      >
        <FormField label={t('audit.filter.action')} fieldId="audit-filter-action">
          <Input
            id="audit-filter-action"
            type="text"
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.currentTarget.value })}
            placeholder={t('audit.filter.action.placeholder')}
            data-testid="audit-filter-action"
            className="w-56"
          />
        </FormField>
        <FormField label={t('audit.filter.target_type')} fieldId="audit-filter-target-type">
          <Select
            value={draft.targetType.length > 0 ? draft.targetType : ANY_TARGET_TYPE}
            onValueChange={(v) =>
              setDraft({ ...draft, targetType: v === ANY_TARGET_TYPE ? '' : v })
            }
            ariaLabel={t('audit.filter.target_type')}
            testId="audit-filter-target-type"
            className="w-48"
            options={TARGET_TYPE_OPTIONS.map((opt) => ({
              value: opt.value === '' ? ANY_TARGET_TYPE : opt.value,
              label: t(opt.labelKey),
            }))}
          />
        </FormField>
        <FormField label={t('audit.filter.target_id')} fieldId="audit-filter-target-id">
          <Input
            id="audit-filter-target-id"
            type="text"
            mono
            value={draft.targetId}
            onChange={(e) => setDraft({ ...draft, targetId: e.currentTarget.value })}
            placeholder={t('audit.filter.target_id.placeholder')}
            data-testid="audit-filter-target-id"
            className="w-64"
          />
        </FormField>
        <FormField label={t('audit.filter.ops_user_id')} fieldId="audit-filter-ops-user">
          <Input
            id="audit-filter-ops-user"
            type="text"
            mono
            value={draft.opsUserId}
            onChange={(e) => setDraft({ ...draft, opsUserId: e.currentTarget.value })}
            placeholder={t('audit.filter.ops_user_id.placeholder')}
            data-testid="audit-filter-ops-user"
            className="w-52"
          />
        </FormField>
        <div className="flex items-center gap-2 pb-px">
          <Button type="submit" variant="primary" data-testid="audit-filter-apply">
            {t('audit.filter.apply')}
          </Button>
          <Button type="button" onClick={handleReset} data-testid="audit-filter-reset">
            {t('audit.filter.reset')}
          </Button>
        </div>
      </form>

      {query.isError ? (
        <div
          role="alert"
          data-testid="audit-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim px-5 py-4"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-semibold text-bad-bright">
              {t('audit.error.title')}
            </span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button type="button" onClick={() => void query.refetch()} data-testid="audit-retry">
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="audit-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-raised" aria-hidden="true" />
          ))}
          <span className="sr-only">{t('audit.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState
            testId="audit-empty"
            title={t('audit.empty.title')}
            hint={t('audit.empty.body')}
          />
        </div>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <Table testId="audit-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('audit.column.when')}</Th>
                <Th>{t('audit.column.action')}</Th>
                <Th>{t('audit.column.target_type')}</Th>
                <Th>{t('audit.column.target_id')}</Th>
                <Th>{t('audit.column.ops_user')}</Th>
                <Th className="text-right" />
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <AuditRow
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={handleToggle}
                />
              ))}
            </TBody>
          </Table>
          <nav
            aria-label={t('accounts.pagination.aria_label')}
            data-testid="audit-pagination"
            className="flex items-center justify-between gap-4 rounded-lg border border-line-soft bg-card px-5 py-3"
          >
            <span className="font-mono text-label text-paper-low">
              {t('audit.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="audit-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="audit-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}
